import type { Action } from 'vuex'
import type { SendCartDestinationSelectionErrors, State } from './state'
import type {
  DestinationDetail,
  DestinationDetailList,
} from '@/api/send_cart/model/sendCartModel'
import type { FolderList, FolderTreeItem } from '@/api/model/folderModel'
import type {
  Client,
  ResponsiblePerson,
} from '@/hooks/services/send-cart/useDestinationSelection'

import actionTypes from './actionTypes'
import {
  queryDestinationDetailList,
  queryDestinationFolderTree,
} from '@/api/send_cart/send_cart'
import { formatFolderList, isObjectEmpty } from '@/utils'
import { SendCartTypeEnum } from '@/api/send_cart/model/sendCartModel'

const actions: { [actionTypes: string]: Action<State, any> } = {
  [actionTypes.SET_SEND_CART_TYPE]({ commit }, type: SendCartTypeEnum): void {
    commit(actionTypes.SET_SEND_CART_TYPE, type)
  },

  [actionTypes.SET_DESTINATION_DETAIL_LIST]: async (
    { commit, dispatch },
    destinationID: string | number
  ): Promise<void> => {
    const list: DestinationDetailList = await queryDestinationDetailList(destinationID)
    const newList = list.filter((data) => data.client_code !== '')
    commit(actionTypes.SET_DESTINATION_DETAIL_LIST, newList)
    dispatch(actionTypes.SET_CLIENT_LIST, newList)
  },

  [actionTypes.REMOVE_DESTINATION_DETAIL_LIST]: ({ commit }): void => {
    commit(actionTypes.SET_DESTINATION_DETAIL_LIST, [])
  },

  [actionTypes.SET_CLIENT_LIST]: ({ commit }, list: DestinationDetailList): void => {
    commit(actionTypes.SET_CLIENT_LIST, list)
  },

  [actionTypes.SELECT_CLIENT]: ({ commit, state }, client: Client): void => {
    const target: DestinationDetail | undefined = state.destinationDetailList.find(
      (item: DestinationDetail) =>
        item.client_code === client.clientCode &&
        item.recipient_detail_id === client.destinationDetailID
    )

    commit(actionTypes.SELECT_CLIENT, target ?? {})
  },

  [actionTypes.SET_RESPONSIBLE_PERSON_LIST]: (
    { commit },
    list: DestinationDetailList
  ): void => {
    commit(actionTypes.SET_RESPONSIBLE_PERSON_LIST, list)
  },

  [actionTypes.SELECT_RESPONSIBLE_PERSON]: (
    { commit, state },
    person: ResponsiblePerson
  ): void => {
    const target: DestinationDetail | undefined = state.destinationDetailList.find(
      (item: DestinationDetail) =>
        item.recipient_user_id === person.responsiblePersonID &&
        item.recipient_detail_id === person.destinationDetailID
    )

    commit(actionTypes.SELECT_RESPONSIBLE_PERSON, target ?? {})
  },

  [actionTypes.SET_DESTINATION_FOLDER_TREE]: async (
    { commit },
    corporateNumber: number
  ): Promise<void> => {
    const list: FolderList = await queryDestinationFolderTree(corporateNumber)
    commit(actionTypes.SET_DESTINATION_FOLDER_TREE, formatFolderList(list))
  },

  [actionTypes.REMOVE_DESTINATION_FOLDER_TREE]: ({ commit }): void => {
    commit(actionTypes.SET_DESTINATION_FOLDER_TREE, [])
  },

  [actionTypes.SELECT_DESTINATION_FOLDER]: (
    { commit, state },
    folder: FolderTreeItem
  ): void => {
    const map: Map<number, FolderTreeItem> = new Map()

    const flatter = (item: FolderTreeItem) => {
      map.set(item.id, item)
      item.children?.forEach(flatter)
    }

    state.destinationFolderTree.forEach(flatter)

    commit(actionTypes.SELECT_DESTINATION_FOLDER, map.get(folder.id) ?? {})
  },

  [actionTypes.SET_ERROR]: (
    { commit, state },
    errors: SendCartDestinationSelectionErrors
  ): void => {
    const _errors = isObjectEmpty(errors) ? errors : { ...state.errors, ...errors }
    commit(actionTypes.SET_ERROR, _errors)
  },
}

export default actions
